import 'react-dates/initialize'
import 'react-dates/lib/css/_datepicker.css'

import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import moment, { Moment } from 'moment-timezone'
import React, { useEffect, useState } from 'react'
import {
  AnchorDirectionShape,
  isInclusivelyBeforeDay,
  OpenDirectionShape,
  SingleDatePicker,
} from 'react-dates'
import { useSelector } from 'react-redux'
import ErrorList from 'src/components/ErrorList/ErrorList'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import SvgCalendar from 'src/components/Svgs/SvgCalendar'
import SvgChevronLeft from 'src/components/Svgs/SvgChevronLeft'
import SvgChevronRight from 'src/components/Svgs/SvgChevronRight'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

interface SingleDateInputFieldProps {
  id?: string
  className?: string
  fieldLabel?: React.ReactNode
  errors?: Array<React.ReactElement | string>
  dateMoment?: Moment
  onDateMomentChange: (val: Moment | undefined) => void
  showUpdates?: boolean
  baseValue?: Moment
  tooltip?: React.ReactElement
  allowAllDates?: boolean
  allowPastAndCurrentDates?: boolean
  placeHolder?: string
  isStatic?: boolean
  /** Marks the underlying date input as required so assistive tech announces
   * it (sets the native `required` attribute, which implies `aria-required`).
   * Pair with a visible required cue in `fieldLabel`. */
  required?: boolean
  /**
   * Which way the calendar opens. Defaults to `'down'` (react-dates' default,
   * and this component's long-standing behaviour). Pass `'up'` where the field
   * sits low in a scroll container — e.g. near the bottom of a flyout — and a
   * downward calendar would render past the container's edge.
   */
  openDirection?: OpenDirectionShape
  /**
   * Which edge the calendar is anchored to. Defaults to `'left'` (calendar
   * extends rightward from the field). Pass `'right'` for a field in the
   * right-hand column of a narrow container, so the calendar extends leftward
   * and stays inside it.
   */
  anchorDirection?: AnchorDirectionShape
}

/**
 * Height of a six-week calendar grid, in px — the tallest a month can render.
 *
 * A month occupies 4, 5, or 6 week rows, and react-dates sizes the popup to
 * whatever the current month needs: measured across 39 consecutive month views
 * the grid only ever takes three heights, 266 / 304 / 342, a uniform 38px per
 * row apart. Downward-opening calendars are top-anchored, so that variation
 * pushes the bottom edge and nobody notices. An UPWARD calendar is bottom-
 * anchored, so the same variation moves the TOP — the month caption and the
 * prev/next arrows jump vertically as you page through months, and the arrow
 * you are clicking slides out from under the pointer.
 *
 * Pinning the grid to its six-row height fixes that: every month renders the
 * same size, top-aligned, so the arrows never move. This is the standard
 * treatment — MUI X exposes it as `fixedWeekNumber`, react-day-picker as
 * `fixedWeeks`, and Google/Apple Calendar both render a constant six-row grid.
 * react-dates has no such prop, so it is done here in CSS.
 *
 * `min-height`, not `height`, so a taller grid (a bigger `daySize`, a future
 * react-dates change) still grows rather than being clipped.
 */
const CALENDAR_FIXED_HEIGHT = 342

const SingleDateInputField: React.FC<SingleDateInputFieldProps> = (
  props,
): React.ReactNode => {
  const translate = useTranslate()

  const locale = useSelector(selectNullSafeLocaleFromState)

  const [showErrors, setShowErrors] = useState(props.errors !== undefined)
  useEffect((): void => {
    setShowErrors(props.errors !== undefined)
  }, [props.errors])

  const onDateChange = (date: Moment | null): void => {
    setShowErrors(false)
    props.onDateMomentChange(date || undefined)
  }

  const [focusedInput, setFocusedInput] = useState<boolean | null>(null)
  const onFocusChange = (arg: { focused: boolean | null }): void => {
    setFocusedInput(arg.focused)
  }

  const isUpdated =
    !!props.showUpdates &&
    props.baseValue?.dayOfYear() !== props.dateMoment?.dayOfYear()

  return (
    <div
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      {props.fieldLabel !== undefined && (
        <FieldLabel
          css={css`
            display: flex;
            align-items: center;
          `}
          isUpdated={isUpdated}
          dontHideOverflow
        >
          {!isUpdated || (isUpdated && props.baseValue === undefined) ? (
            <>{props.fieldLabel}</>
          ) : (
            props.baseValue !== undefined &&
            translate('${fieldLabel} (was ${baseValue})', {
              fieldLabel: props.fieldLabel,
              baseValue: props.baseValue?.format('ll'),
            })
          )}
          {props.tooltip !== undefined && props.tooltip}
        </FieldLabel>
      )}
      {props.isStatic ? (
        <span
          css={css`
            ${getFontCss(
              14,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )}
          `}
        >
          {props.dateMoment !== undefined
            ? props.dateMoment?.format('MMM D, YYYY')
            : '--'}
        </span>
      ) : (
        <div
          data-testid={props.id ?? 'calendarInputField'}
          css={css`
            width: 100%;

            .SingleDatePicker {
              width: 100%;
            }

            .SingleDatePickerInput {
              display: flex;
              width: 100%;
              border: 1px solid ${styleConstants.colors.gray_medium};
              background-color: ${styleConstants.colors.gray_lightest};
              border-radius: 8px;

              &:focus,
              &:focus-within,
              &:hover {
                background-color: ${styleConstants.colors.white};
                border: 1px solid ${themeConstants.colors.cta};
              }
            }

            ${isUpdated &&
            css`
              .SingleDatePickerInput {
                border: 1px solid ${themeConstants.colors.highlight};
              }
            `}

            ${props.errors !== undefined &&
            showErrors &&
            css`
              .SingleDatePickerInput {
                border: 1px solid ${styleConstants.colors.red};
              }
            `}

        .SingleDatePickerInput_calendarIcon {
              padding: 10px 0px 10px 10px;
              margin: 0px;
            }

            .DateInput_1 {
              background-color: inherit;
              flex-grow: 2;
              border-radius: 8px;
            }

            .DateInput_input {
              margin: 0px;
              height: 48px;
              caret-color: ${themeConstants.colors.cta};
              ${getFontCss(
                14,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
              background-color: inherit;
              border: none;
              border-radius: 8px;
              padding-bottom: 14px;

              &::placeholder {
                opacity: 0.5;
                ${getFontCss(
                  14,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.regular,
                )};
              }
            }

            ${isUpdated &&
            css`
              .DateInput_input {
                ${getFontCss(
                  14,
                  themeConstants.colors.highlight,
                  styleConstants.fontFamilies.regular,
                )};
              }
            `}

            ${props.errors !== undefined &&
            showErrors &&
            css`
              .DateInput_input {
                ${getFontCss(
                  14,
                  styleConstants.colors.red,
                  styleConstants.fontFamilies.regular,
                )};
              }
            `}

        .DateInput_fang {
              display: none;
            }

            .SingleDatePickerInput_arrow {
              display: none;
            }

            /* react-dates positions the popup itself; this pins it flush to
               the field. The left override has to yield when the caller
               anchors right, otherwise "left: 0 !important" beats the right
               offset react-dates sets and the calendar stays left-aligned
               (i.e. anchorDirection would silently do nothing). */
            .SingleDatePicker_picker {
              ${props.anchorDirection === 'right'
                ? css`
                    left: auto !important;
                    right: 0px !important;
                  `
                : css`
                    left: 0px !important;
                  `}
              z-index: 2;
            }

            .DayPicker {
              border-radius: 8px;
              border: 1px solid ${themeConstants.colors.cta};
              overflow: hidden;
              ${props.openDirection === 'up' &&
              css`
                min-height: ${CALENDAR_FIXED_HEIGHT}px;
              `}
            }

            .CalendarMonth_caption {
              ${getFontCss(
                14,
                themeConstants.colors.cta,
                styleConstants.fontFamilies.medium,
              )};
              padding-bottom: 48px;
            }

            .CalendarDay {
              border: 1px solid ${styleConstants.colors.gray_medium};
              background-color: ${styleConstants.colors.gray_lightest};

              &:hover {
                background-color: ${themeConstants.colors.cta_dark};
                ${getFontCss(
                  14,
                  styleConstants.colors.white,
                  styleConstants.fontFamilies.regular,
                )};
              }
            }

            .CalendarDay__blocked_out_of_range {
              ${getFontCss(
                14,
                styleConstants.colors.gray_dark,
                styleConstants.fontFamilies.regular,
              )};
              &:hover {
                background-color: ${styleConstants.colors.gray_lightest};
                ${getFontCss(
                  14,
                  styleConstants.colors.gray_dark,
                  styleConstants.fontFamilies.regular,
                )};
              }
            }

            .CalendarDay__selected {
              background-color: ${themeConstants.colors.cta_dark};
            }

            .CalendarDay__hovered_span,
            .CalendarDay__selected_span {
              background-color: ${themeConstants.colors.cta_dark};
              opacity: 0.5;
              color: ${styleConstants.colors.white};
            }

            .DayPickerKeyboardShortcuts_buttonReset {
              display: none;
            }

            .DayPickerNavigation {
              position: absolute;
              top: 16px;
              padding: 0px 24px;
              display: flex;
              width: 100%;
              justify-content: space-between;
            }
          `}
        >
          <SingleDatePicker
            id={props.id ?? 'calendarInputField'}
            required={props.required}
            isOutsideRange={
              props.allowAllDates
                ? (): boolean => false
                : props.allowPastAndCurrentDates
                  ? (day: moment.Moment): boolean => {
                      return !isInclusivelyBeforeDay(day, moment())
                    }
                  : undefined
            }
            placeholder={
              props.placeHolder ||
              translateSingleString(locale, 'Select a date')
            }
            date={props.dateMoment || null}
            onDateChange={onDateChange}
            openDirection={props.openDirection}
            anchorDirection={props.anchorDirection}
            displayFormat={'ll'}
            focused={!!focusedInput}
            onFocusChange={onFocusChange}
            initialVisibleMonth={(): Moment => props.dateMoment || moment()}
            numberOfMonths={1}
            navPrev={
              <SvgChevronLeft
                data-testid={'DateInputFieldPreviousBtn'}
                css={css`
                  transform: scale(1.2);
                `}
              />
            }
            navNext={
              <SvgChevronRight
                data-testid={'DateInputFieldNextBtn'}
                css={css`
                  transform: scale(1.2);
                `}
              />
            }
            customInputIcon={<SvgCalendar />}
          />
        </div>
      )}
      {showErrors && props.errors !== undefined && (
        <ErrorList errors={props.errors} />
      )}
    </div>
  )
}

export default React.memo(SingleDateInputField)
